// Reusable row-level UI primitives for portal listings (advisors, clients,
// team members, future admin tables). Imports tokens directly so the buttons
// stay visually locked to the design system regardless of where they're used.

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { C, F, useIsMobile } from './tokens';

// Compact uppercase action button used in list-row action clusters. Two
// variants only — primary (filled navy) for the dominant action on a row,
// secondary (outlined navy) for everything else. Fixed 30px height so a row
// of mixed primary + secondary buttons aligns cleanly.
export function ActionButton({ label, onClick, variant = 'secondary', disabled = false, title, block = false, size = 'sm' }) {
  // 'warning' added for the client-list Preview affordance (amber). Other
  // call sites use 'primary' (filled navy) or 'secondary' (outlined navy);
  // those two are unchanged so Advisors rows render byte-identically.
  const variantStyle = variant === 'primary'
    ? { background: C.navy, color: '#fff', border: `1px solid ${C.navy}` }
    : variant === 'warning'
    ? { background: C.amberBg, color: C.amber, border: `1px solid ${C.amberBd}` }
    : { background: C.surface, color: C.navy, border: `1px solid ${C.navyBorder}` };

  // Default ('sm') reproduces the original ActionButton metrics exactly so
  // existing call sites (Advisors rows in App.js) render unchanged. 'lg' is
  // the touch-friendly variant used in mobile card layouts.
  const sizeStyle = size === 'lg'
    ? { height: 44, padding: '10px 14px', fontSize: 12 }
    : { height: 30, padding: '6px 14px', fontSize: 11 };

  const blockStyle = block ? { width: '100%', flex: 1 } : null;

  const style = {
    ...variantStyle,
    ...sizeStyle,
    ...(blockStyle || {}),
    borderRadius: 6,
    fontFamily: F.body,
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: disabled ? 'default' : 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    opacity: disabled ? 0.5 : 1,
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={style}
    >
      {label}
    </button>
  );
}

// Kebab menu for collapsing secondary / destructive row actions on mobile.
// items: [{ label, onClick, danger? }, ...]. The dropdown anchors to the
// right edge of the trigger, closes on outside click and Escape.
export function OverflowMenu({ items, variant = 'secondary', title, subtitle }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) return;
    // Mobile sheet is portaled to document.body (outside containerRef), so a
    // document-level mousedown handler would treat sheet items as "outside"
    // and unmount the portal before the item's click event fires. The sheet's
    // own overlay onClick handles tap-to-close, so skip this listener on mobile.
    function handleKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    if (isMobile) {
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    function handleMouseDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, isMobile]);

  const triggerStyle = variant === 'primary'
    ? { background: C.navy, border: `1px solid ${C.navy}` }
    : { background: C.surface, border: `1px solid ${C.navyBorder}` };
  const dotFill = variant === 'primary' ? '#fff' : C.navy;

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="More actions"
        style={{
          ...triggerStyle,
          width: 44,
          height: 44,
          borderRadius: 6,
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}>
        <svg width="4" height="18" viewBox="0 0 4 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="2" cy="2" r="2" fill={dotFill}/>
          <circle cx="2" cy="9" r="2" fill={dotFill}/>
          <circle cx="2" cy="16" r="2" fill={dotFill}/>
        </svg>
      </button>
      {open && !isMobile && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 180,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.08)',
            zIndex: 20,
            overflow: 'hidden',
          }}>
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              onClick={() => { item.onClick && item.onClick(); setOpen(false); }}
              style={{
                width: '100%',
                height: 44,
                padding: '0 14px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                fontFamily: F.body,
                fontSize: 13,
                color: item.danger ? C.red : C.charcoal,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}>
              {item.label}
            </button>
          ))}
        </div>
      )}
      {open && isMobile && typeof document !== 'undefined' && createPortal(
        <>
          {/* Dim overlay — tap-anywhere-outside-the-sheet to close. Rendered
              via portal to document.body so it escapes any ancestor stacking
              context (row/card containers, AppShell <main>, etc.) and reliably
              sits above the fixed BottomNav (zIndex 50). */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.35)',
              zIndex: 2000,
            }}
          />
          {/* Bottom sheet. stopPropagation on click so taps inside the
              sheet (grab handle, padding, etc.) don't bubble to the
              overlay's close handler. Item taps still close via the
              item's own onClick. */}
          <div
            role="menu"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed', bottom: 0, left: 0, right: 0,
              background: C.surface,
              borderRadius: '18px 18px 0 0',
              paddingBottom: 14,
              zIndex: 2001,
            }}>
            <div style={{
              width: 36, height: 4,
              background: C.border,
              borderRadius: 2,
              margin: '10px auto 6px',
            }}/>
            {/* Optional header — mobile-only. When present, the header's
                own borderBottom provides separation from the first action,
                so first-item borderTop stays 'none' as below. */}
            {title && (
              <div style={{ borderBottom: `1px solid ${C.border}` }}>
                <div style={{
                  fontFamily: F.body, fontSize: 17, fontWeight: 700,
                  color: C.charcoal, padding: '4px 20px 2px',
                }}>
                  {title}
                </div>
                {subtitle && (
                  <div style={{
                    fontFamily: F.body, fontSize: 12, color: C.charMid,
                    padding: '0 20px 12px',
                  }}>
                    {subtitle}
                  </div>
                )}
              </div>
            )}
            {items.map((item, i) => (
              <button
                key={i}
                type="button"
                role="menuitem"
                onClick={() => { item.onClick && item.onClick(); setOpen(false); }}
                style={{
                  width: '100%',
                  height: 56,
                  padding: '0 20px',
                  background: 'transparent',
                  border: 'none',
                  borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                  textAlign: 'left',
                  fontFamily: F.body,
                  fontSize: 15,
                  color: item.danger ? C.red : C.charcoal,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                {item.label}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
